import React, { useMemo } from "react";
import { currentCompassDirection } from "@/lib/compassDirections";

/**
 * CompassReadingCard — the copper "Your Compass Today" surface that sits
 * under the Soul Compass entry card. It renders the direction chosen for
 * today (deterministic by day-of-year via `currentCompassDirection`), with
 * theme, tone, and a short message.
 *
 * Design intent (matches reference): a soft copper gradient card with
 * warm-light glow, a hairline arc suggesting compass geometry, a large
 * cream headline "Your Compass Today: <Direction> — <Theme>", the tone
 * line, and a short italic message.
 */
export default function CompassReadingCard() {
  const d = useMemo(() => currentCompassDirection(), []);

  return (
    <section
      className="relative overflow-hidden rounded-2xl px-6 py-8 md:px-10 md:py-10 text-center"
      style={{
        background:
          "linear-gradient(135deg, #C56B3F 0%, #C48469 45%, #F0C089 100%)",
        boxShadow: "0 24px 60px -30px rgba(184, 115, 85, 0.55)",
      }}
      data-testid="compass-reading-card"
    >
      {/* Subtle geometry — a hairline arc that reads as a compass ring. */}
      <div
        aria-hidden
        className="absolute inset-x-6 top-4 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(246,241,232,0.35), transparent)" }}
      />
      <div
        aria-hidden
        className="absolute inset-x-6 bottom-4 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(246,241,232,0.35), transparent)" }}
      />
      {/* Little cardinal dots left / right. */}
      <div
        aria-hidden
        className="absolute top-1/2 left-4 -translate-y-1/2 w-1.5 h-1.5 rounded-full pointer-events-none"
        style={{ background: "rgba(246,241,232,0.7)", boxShadow: "0 0 8px rgba(246,241,232,0.5)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 right-4 -translate-y-1/2 w-1.5 h-1.5 rounded-full pointer-events-none"
        style={{ background: "rgba(246,241,232,0.7)", boxShadow: "0 0 8px rgba(246,241,232,0.5)" }}
      />

      <div
        className="relative max-w-2xl mx-auto"
        style={{ animation: "reveal 700ms ease-out both" }}
      >
        <h3
          className="font-display text-2xl md:text-3xl leading-tight"
          style={{ color: "#F6F1E8" }}
          data-testid="compass-reading-headline"
        >
          Your Compass Today: {d.label}{" "}
          <span className="opacity-90">— {d.theme}</span>
        </h3>
        <p
          className="mt-5 text-sm md:text-base"
          style={{ color: "rgba(246,241,232,0.9)" }}
          data-testid="compass-reading-tone"
        >
          <span className="opacity-80">Tone:</span> {d.tone}
        </p>
        <p
          className="mt-3 font-display italic text-lg md:text-xl leading-snug"
          style={{ color: "#F6F1E8" }}
          data-testid="compass-reading-message"
        >
          {d.message}
        </p>
      </div>
    </section>
  );
}
